import React from "react"
import { withRouter } from "next/router"
import { inject, observer } from "mobx-react"
import dynamic from "next/dynamic"
import styled from "styled-components"

import { getStepFromSlug } from "~/components/traffic-tickets/steps/steps"
import Layout from "~/components/traffic-tickets/LandingLayout"
import CheckoutLayout from "~/components/traffic-tickets/steps/PaymentDetails/CheckoutLayout"
import BackgroundSection from "~/components/traffic-tickets/BackgroundSection"
import Footer from "~/components/Footer"
import Spacer from "~/components/Spacer"
import InitializeScreen from "~/components/traffic-tickets/screens/initialize"
import Wisepops from "~/components/_integrations/Wisepops"

import questionMarkIcon from "~/public/static/images/svg/questionMarkIcon.svg"
import {STATES} from "~/utilities/constants"

// Todo: clean up JSX / styled-components => Move logic & code unrelated to the "page" to components

const FAQModalContainer = dynamic(() =>
  import("~/components/traffic-tickets/FAQ/ModalContainer")
)

const StateName = styled.h1`
  margin: 0px 20px;
  height: min(4vw, 85px);
  color: #3d3c3c;
  font-family: ${(props) => props.theme.fonts.avenirNextDemi};
  text-align: center;
  white-space: nowrap;
  font-size: min(2vw, 36px);
  ${(props) => props.theme.media.smallDesktop`
    align-items:center;
    margin: 0px 0px 17px 0px;
    height: initial;
    font-size: 30px;
  `}
  ${(props) => props.theme.media.mobile`
    font-size: 28px;
  `}
`

const StepContainer = styled.div`
  margin: 32px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: min(100%, 1300px);
  ${(props) => props.theme.media.smallDesktop`
    flex-direction: column;
    align-items: center;
    margin: 0px 0px 32px 0px;
  `}
`

const LeftBoxContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  ${(props) => props.theme.media.smallDesktop`
    display: none;
  `}
`

const Left = styled.div`
  position: relative;
  text-align: "left";
`

const Right = styled.div`
  margin-right: 40px;
  box-shadow: 0px 9px 24px rgba(35, 41, 48, 0.25);
  border-radius: 5px;
  max-height: 450px;
  margin-bottom: 10%;

  ${(props) => props.theme.media.smallDesktop`
    width: ${(innerProps) => (innerProps.fullWidth ? "100%" : "90%")};
    margin: 0px 0px 30px 0px; /* ✅ Adds bottom margin on smaller screens */
    box-shadow: none;
    max-height: initial;
  `}
`

const GoodNews = styled.div`
  color: #2b2b2b;
  font-family: ${(props) => props.theme.fonts.avenirNextHeavy};
  font-size: 40px;
  text-align: center;
  margin-bottom: 20px;
`

const AvailableAttorneys = styled.div`
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0px 9px 24px rgba(35, 41, 48, 0.25);
  padding: 7px;
  margin: 0px 20px;
  color: #2b2b2b;
  font-family: ${(props) => props.theme.fonts.avenirNextDemi};
  font-size: 20px;
  line-height: 33px;
  text-align: center;
  margin: 0 auto;
  width: 325px;
  ${(props) => props.theme.media.smallDesktop`
    font-size: min(5vw, 20px);
    padding: 0.5em;
    width: 100%;
  `};
  ${(props) => props.theme.media.mobile`
    width: 90%;
    line-height: 25px;
    padding: 0.25em;
  `};
`

const FAQ = styled.div`
  margin: 20px 20px 0px 0px;
`

const QuestionMark = styled.img`
  position: absolute;
  right: -4px;
  top: -15px;
`

const ModalOpener = styled.div`
  width: 180px;
  align-self: flex-start;
  margin: 22px 0px 22px 0px;
  padding: 5px 10px;
  background: #ffffff;
  box-shadow: 0px 9px 24px rgba(35, 41, 48, 0.15);
  position: relative;
  color: #40505a;
  font-family: ${(props) => props.theme.fonts.avenirNextDemi};
  font-size: 12px;
  line-height: 29px;
  border-radius: 10px;
  text-align: center;
  cursor: pointer;
  ${(props) => props.theme.media.smallDesktop`
    padding: 0px;
  `}
`

@inject((all) => ({
  isMobile: all.store.ui.isMobile,
  isTablet: all.store.ui.isTablet,
  isSmallDesktop: all.store.ui.isSmallDesktop,
  trafficSubmission: all.store.trafficSubmission,
  step: all.store.trafficSubmission.step,
  selectedState: all.store.trafficSubmission.selectedState,
}))
@observer
class TrafficSubmissionComponent extends React.Component {
  componentDidMount = async () => {
    if (process.browser) {
      this.props.trafficSubmission.loadImages()
      window.scrollTo({ top: 0, behavior: "smooth" })
      if (this.props.trafficSubmission.initialized) {
        this.props.trafficSubmission.reset()
      }
      await this.props.trafficSubmission.initialize()

      document.addEventListener("keyup", this.handleNextStepKey)
    }
  }

  componentDidUpdate = async (prevProps) => {
    if (prevProps.router.pathname !== this.props.router.pathname) {
      this.props.trafficSubmission.reset()
      this.props.trafficSubmission.loadImages()
      window.scrollTo({ top: 0, behavior: "smooth" })
      await this.props.trafficSubmission.initialize()
    }
  }

  componentWillUnmount() {
    this.props.trafficSubmission.reset()
    document.removeEventListener("keyup", this.handleNextStepKey)
  }

  handleNextStepKey = (e) => {
    if (e.key === "Enter") this.props.trafficSubmission.nextStep()
  }

  render() {
    const {
      step,
      selectedState,
      trafficSubmission,
      isMobile,
      isSmallDesktop,
    } = this.props
    const StepToRender = getStepFromSlug(step,trafficSubmission.selectedState.available)
    const { slug } = this.props.router.query
    const state = slug?.replace('-traffic-tickets', '')
    let fullStateName = selectedState.longName
    if (state) {
      fullStateName = STATES[state.toUpperCase()]
    }

    let quote
    if (this.props.trafficSubmission.quotes.length) {
      // MST throws a warning when we don't check this
      quote = this.props.trafficSubmission.quotes[0] // todo: satisfy eslint-prefer-destructuring
    }

    if (!StepToRender || !StepToRender.Component) {
      return (
        <>
          <InitializeScreen />
        </>
      )
    }

    if (
      StepToRender.slug === "payment-details" ||
      StepToRender.slug === "done"
    ) {
      return (
        <>
          <CheckoutLayout key="step">
            <StepToRender.Component />
          </CheckoutLayout>
        </>
      )
    }

    let CallToAction = (
      <StateName>
        Fight Your {isMobile ? <br /> : null} {fullStateName} Traffic
        Tickets
      </StateName>
    )

    if (isSmallDesktop && StepToRender.slug === "attorney") {
      CallToAction = (
        <>
          <GoodNews>Good News</GoodNews>
          <AvailableAttorneys>
            {trafficSubmission.quotes.length > 1
              ? "These attorneys are"
              : "This attorney is"}
            &nbsp;available to fight your ticket
          </AvailableAttorneys>
        </>
      )
    }

    let FAQComponent = null
    if (StepToRender.slug === "attorney") {
      FAQComponent = (
        <FAQ>
          <FAQModalContainer
            quote={quote}
            LaunchButton={(props) => {
              return (
                <ModalOpener {...props}>
                  Frequently Asked Questions
                  <QuestionMark src={questionMarkIcon} />
                </ModalOpener>
              )
            }}
          />
        </FAQ>
      )
    }

    return (
      <>
        <h1 style={{ display: "none" }}>Fight Traffic Tickets</h1> {/* SEO */}
        <Layout key="step" inFlow>
          <BackgroundSection>
            <Spacer mobile={15} desktop={15} />
            <StepContainer>
              <Left>
                {CallToAction}
                <LeftBoxContainer>
                  <StepToRender.Component />
                  {FAQComponent}
                </LeftBoxContainer>
              </Left>
              {(isMobile || isSmallDesktop) && (
                <Right fullWidth={StepToRender.slug === "attorney"}>
                  <StepToRender.Component />
                </Right>
              )}
            </StepContainer>
            <Spacer mobile={15} desktop={55} />
          </BackgroundSection>
          <Footer noDisclaimer noMedia />
        </Layout>
        <Wisepops />
      </>
    )
  }
}

export default withRouter(TrafficSubmissionComponent)
